import React, { useState, useRef, useContext } from 'react';
import styles from './CreateTenant.module.scss';
import classNames from 'classnames/bind';
import { Button } from '@components';
import { Typography, Input } from 'gd-design-library';
import { useServiceRef } from 'hooks';
import { TenantService } from '@services/tenant';
import { Tenant } from '@utils/TenantContext';
import { SnackbarContext } from '@utils';

const cx = classNames.bind(styles);

const CN = 'create-tenant';
const createTenantInputsClassName = `${CN}__inputs`;
const errorClassName = `${CN}__error`;

interface CreateTenantProps {
  setTenants: (tenants: Tenant[]) => void;
}

export const CreateTenant = ({ setTenants }: CreateTenantProps): React.ReactElement => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const tenantServiceRef = useServiceRef(TenantService);
  const { setSnackbarParams } = useContext(SnackbarContext);
  const [ id, setId ] = useState<string>('');
  const [ file, setFile ] = useState<File>(null);
  const [ errors, setErrors ] = useState({ id: '', file: '' });
  const [ isCreateLoading, setIsCreateLoading ] = useState(false);

  const resetForm = (): void => {
    setId('');
    setFile(null);
    setErrors({ id: '', file: '' });
  };

  const createTenant = async (): Promise<void> => {
    const newErrors = { id: !id ? 'ID is required' : '', file: !file ? 'File is required' : '' };
    setErrors(newErrors);
    if (newErrors.id || newErrors.file) return;

    setIsCreateLoading(true);
    const result = await tenantServiceRef.current.createTenant(id, file);

    if (result) {
      setTenants(result);
      resetForm();
      setSnackbarParams({
        open: true,
        onClose: () => setSnackbarParams({ open: false, message: '', onClose: () => null }),
        message: 'Tenant is being created',
        severity: 'success'
      });
    } else {
      setSnackbarParams({
        open: true,
        onClose: () => setSnackbarParams({ open: false, message: '', onClose: () => null }),
        message: 'Failed to create tenant',
        severity: 'error'
      });
    }

    setIsCreateLoading(false);
  };

  return (
    <div className={ cx(CN) }>
      <Typography variant='h4'>Create Tenant</Typography>
      <div className={ cx(createTenantInputsClassName) }>
        <Input
          label='ID'
          name='id'
          required
          variant='text'
          value={ id }
          onChange={ (e): void => {
            setId(e.target.value);
            setErrors(prev => ({ ...prev, id: !e.target.value ? 'ID is required' : '' }));
          } }
          color={ errors.id ? 'error' : 'primary' }
          helperText={ errors.id }
          disabled={ isCreateLoading }
        />
        <input
          type='file'
          name='file'
          required
          accept='.zip'
          multiple={ false }
          ref={ fileInputRef }
          onChange={ (e): void => {
            setFile(e.target.files?.[0]);
            setErrors(prev => ({ ...prev, file: !e.target.files?.[0] ? 'File is required' : '' }));
          } }
        />
        <Button
          variant='outlined'
          onClick={ (): void => fileInputRef.current?.click() }
          className={ errors.file ? cx(errorClassName) : '' }
          disabled={ isCreateLoading }
        >
          {file?.name || 'Select File'}
        </Button>
      </div>
      <Button onClick={ (): void => { createTenant(); } } loading={ isCreateLoading }>
        {isCreateLoading ? 'Creating...' : 'Create'}
      </Button>
    </div>
  );
};
